import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText, Settings, LogOut, TrendingUp, Mail, CreditCard, FileSignature,
  Check, X, ExternalLink, Shield, AlertTriangle, Loader2, Plug
} from "lucide-react";
import Sidebar from "@/components/Sidebar";

const Integrations = () => {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [disconnecting, setDisconnecting] = useState(null); // Track which integration is pending disconnect
  const [integrations, setIntegrations] = useState({
    email: { connected: false },
    docusign: { connected: false },
    stripe: { connected: false }
  });
  
  // SMTP Form State
  const [smtpForm, setSmtpForm] = useState({
    host: "",
    port: 587,
    username: "",
    password: "",
    use_tls: true,
    from_email: "",
    from_name: ""
  });
  
  // SendGrid Form State
  const [sendgridForm, setSendgridForm] = useState({
    api_key: "",
    from_email: "",
    from_name: ""
  });
  
  const [showSmtpDialog, setShowSmtpDialog] = useState(false);
  const [showSendgridDialog, setShowSendgridDialog] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };
  const hasHandledCallback = useRef(false);

  useEffect(() => {
    // Handle OAuth callback success/error from URL (only once)
    const success = searchParams.get("success");
    const error = searchParams.get("error");
    const provider = searchParams.get("provider");
    
    if (!hasHandledCallback.current && (success || error)) {
      hasHandledCallback.current = true;
      
      if (success === "true" && provider) {
        toast.success(`${provider} connected successfully!`);
      } else if (error) {
        toast.error(`Failed to connect: ${error}`);
      }
      
      // Clear URL params without triggering re-render loop
      navigate("/integrations", { replace: true });
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    loadIntegrations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadIntegrations = async () => {
    try {
      const response = await axios.get(`${API}/integrations/status`, { headers, withCredentials: true });
      setIntegrations(response.data);
    } catch (error) {
      console.error("Failed to load integrations:", error);
    } finally {
      setLoading(false);
    }
  };

  const initiateOAuth = async (provider) => {
    setConnecting(provider);
    try {
      const endpoint = {
        google: "/integrations/email/google/authorize",
        microsoft: "/integrations/email/microsoft/authorize",
        docusign: "/integrations/docusign/authorize",
        stripe: "/integrations/stripe/authorize"
      }[provider];
      
      const response = await axios.get(`${API}${endpoint}`, { headers, withCredentials: true });
      
      // Redirect to OAuth provider
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error(`Failed to initiate ${provider} authorization`);
      setConnecting(null);
    }
  };

  const configureSMTP = async () => {
    setConnecting("smtp");
    try {
      await axios.post(`${API}/integrations/email/smtp`, smtpForm, { headers, withCredentials: true });
      toast.success("SMTP configured successfully!");
      setShowSmtpDialog(false);
      loadIntegrations();
    } catch (error) {
      toast.error("Failed to configure SMTP");
    } finally {
      setConnecting(null);
    }
  };

  const configureSendGrid = async () => {
    setConnecting("sendgrid");
    try {
      await axios.post(`${API}/integrations/email/sendgrid`, sendgridForm, { headers, withCredentials: true });
      toast.success("SendGrid configured successfully!");
      setShowSendgridDialog(false);
      loadIntegrations();
    } catch (error) {
      toast.error("Failed to configure SendGrid");
    } finally {
      setConnecting(null);
    }
  };

  const disconnectIntegration = async (type) => {
    try {
      await axios.delete(`${API}/integrations/${type}`, { headers, withCredentials: true });
      toast.success(`${type} disconnected`);
      setDisconnecting(null);
      loadIntegrations();
    } catch (error) {
      toast.error(`Failed to disconnect ${type}`);
      setDisconnecting(null);
    }
  };

  return (
    <div className="min-h-screen bg-sand" data-testid="integrations-page">
      {/* Sidebar Component */}
      <Sidebar user={user} logout={logout} />

      {/* Main Content */}
      <main className="md:ml-52 lg:ml-64 p-4 md:p-8 pt-16 md:pt-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-display text-3xl font-bold text-forest">Integrations</h1>
          <p className="text-muted-foreground mt-1">Connect your services to send emails, contracts, and invoices</p>
        </div>

        {/* Security Notice */}
        <Card className="border-forest/20 bg-forest/5 mb-8">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Shield className="w-6 h-6 text-forest" />
              <div>
                <p className="font-medium text-forest">Your credentials are secure</p>
                <p className="text-sm text-muted-foreground">
                  All API keys and tokens are encrypted with AES-256 before storage. OAuth integrations never expose your passwords to us.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-6">
          {/* Email Integration */}
          <Card className="border-stone">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Mail className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle className="font-display text-xl">Email Service</CardTitle>
                    <CardDescription>Send proposal notifications to prospects</CardDescription>
                  </div>
                </div>
                {integrations.email.connected ? (
                  <Badge className="bg-green-100 text-green-800">
                    <Check className="w-3 h-3 mr-1" />
                    Connected
                  </Badge>
                ) : (
                  <Badge variant="secondary">Not Connected</Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {integrations.email.connected ? (
                <div className="flex items-center justify-between p-4 bg-sand rounded-lg">
                  <div>
                    <p className="font-medium text-forest capitalize">{integrations.email.provider}</p>
                    <p className="text-sm text-muted-foreground">{integrations.email.email}</p>
                  </div>
                  {disconnecting === "email" ? (
                    <div className="flex gap-2">
                      <Button type="button" variant="destructive" size="sm" onClick={() => disconnectIntegration("email")}>
                        Confirm
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={() => setDisconnecting(null)}>
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <Button type="button" variant="outline" onClick={() => setDisconnecting("email")}>
                      Disconnect
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Choose how you want to send emails. OAuth options are recommended as they don't require storing credentials.
                  </p>
                  
                  <Tabs defaultValue="oauth" className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="oauth">OAuth (Recommended)</TabsTrigger>
                      <TabsTrigger value="credentials">API Key / SMTP</TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="oauth" className="space-y-3 mt-4">
                      <Button 
                        variant="outline" 
                        className="w-full justify-start h-14"
                        onClick={() => initiateOAuth("google")}
                        disabled={connecting === "google"}
                        data-testid="connect-google-btn"
                      >
                        {connecting === "google" ? (
                          <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                        ) : (
                          <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                          </svg>
                        )}
                        Connect Google (Gmail)
                      </Button>
                      
                      <Button 
                        variant="outline" 
                        className="w-full justify-start h-14"
                        onClick={() => initiateOAuth("microsoft")}
                        disabled={connecting === "microsoft"}
                        data-testid="connect-microsoft-btn"
                      >
                        {connecting === "microsoft" ? (
                          <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                        ) : (
                          <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                            <path fill="#F25022" d="M1 1h10v10H1z"/>
                            <path fill="#00A4EF" d="M1 13h10v10H1z"/>
                            <path fill="#7FBA00" d="M13 1h10v10H13z"/>
                            <path fill="#FFB900" d="M13 13h10v10H13z"/>
                          </svg>
                        )}
                        Connect Microsoft (Outlook)
                      </Button>
                    </TabsContent>
                    
                    <TabsContent value="credentials" className="space-y-3 mt-4">
                      <Dialog open={showSendgridDialog} onOpenChange={setShowSendgridDialog}>
                        <DialogTrigger asChild>
                          <Button variant="outline" className="w-full justify-start h-14">
                            <Mail className="w-5 h-5 mr-3 text-blue-500" />
                            Configure SendGrid
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle className="font-display">Configure SendGrid</DialogTitle>
                            <DialogDescription>
                              Your API key is encrypted before storage and never logged.
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 mt-4">
                            <div>
                              <Label>SendGrid API Key</Label>
                              <Input
                                type="password"
                                value={sendgridForm.api_key}
                                onChange={(e) => setSendgridForm({...sendgridForm, api_key: e.target.value})}
                                placeholder="SG.xxxxxxxxxx"
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <Label>From Email</Label>
                              <Input
                                type="email"
                                value={sendgridForm.from_email}
                                onChange={(e) => setSendgridForm({...sendgridForm, from_email: e.target.value})}
                                placeholder="you@yourdomain.com"
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <Label>From Name (optional)</Label>
                              <Input
                                value={sendgridForm.from_name}
                                onChange={(e) => setSendgridForm({...sendgridForm, from_name: e.target.value})}
                                placeholder="Your Company"
                                className="mt-1"
                              />
                            </div>
                            <Button 
                              className="w-full bg-forest hover:bg-forest/90"
                              onClick={configureSendGrid}
                              disabled={connecting === "sendgrid"}
                            >
                              {connecting === "sendgrid" ? "Saving..." : "Save Configuration"}
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                      
                      <Dialog open={showSmtpDialog} onOpenChange={setShowSmtpDialog}>
                        <DialogTrigger asChild>
                          <Button variant="outline" className="w-full justify-start h-14">
                            <Mail className="w-5 h-5 mr-3 text-gray-500" />
                            Configure SMTP
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-md">
                          <DialogHeader>
                            <DialogTitle className="font-display">Configure SMTP</DialogTitle>
                            <DialogDescription>
                              Your credentials are encrypted with AES-256 before storage.
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 mt-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label>SMTP Host</Label>
                                <Input
                                  value={smtpForm.host}
                                  onChange={(e) => setSmtpForm({...smtpForm, host: e.target.value})}
                                  placeholder="smtp.gmail.com"
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label>Port</Label>
                                <Input
                                  type="number"
                                  value={smtpForm.port}
                                  onChange={(e) => setSmtpForm({...smtpForm, port: parseInt(e.target.value)})}
                                  placeholder="587"
                                  className="mt-1"
                                />
                              </div>
                            </div>
                            <div>
                              <Label>Username</Label>
                              <Input
                                value={smtpForm.username}
                                onChange={(e) => setSmtpForm({...smtpForm, username: e.target.value})}
                                placeholder="your@email.com"
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <Label>Password / App Password</Label>
                              <Input
                                type="password"
                                value={smtpForm.password}
                                onChange={(e) => setSmtpForm({...smtpForm, password: e.target.value})}
                                placeholder="••••••••"
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <Label>From Email</Label>
                              <Input
                                type="email"
                                value={smtpForm.from_email}
                                onChange={(e) => setSmtpForm({...smtpForm, from_email: e.target.value})}
                                placeholder="you@yourdomain.com"
                                className="mt-1"
                              />
                            </div>
                            <div className="flex items-center justify-between">
                              <Label>Use TLS</Label>
                              <Switch
                                checked={smtpForm.use_tls}
                                onCheckedChange={(checked) => setSmtpForm({...smtpForm, use_tls: checked})}
                              />
                            </div>
                            <Button 
                              className="w-full bg-forest hover:bg-forest/90"
                              onClick={configureSMTP}
                              disabled={connecting === "smtp"}
                            >
                              {connecting === "smtp" ? "Saving..." : "Save Configuration"}
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </TabsContent>
                  </Tabs>
                </div>
              )}
            </CardContent>
          </Card>

          {/* DocuSign Integration */}
          <Card className="border-stone">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <FileSignature className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    <CardTitle className="font-display text-xl">DocuSign</CardTitle>
                    <CardDescription>Send contracts for e-signature when proposals are accepted</CardDescription>
                  </div>
                </div>
                {integrations.docusign.connected ? (
                  <Badge className="bg-green-100 text-green-800">
                    <Check className="w-3 h-3 mr-1" />
                    Connected
                  </Badge>
                ) : (
                  <Badge variant="secondary">Not Connected</Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {integrations.docusign.connected ? (
                <div className="flex items-center justify-between p-4 bg-sand rounded-lg">
                  <div>
                    <p className="font-medium text-forest">DocuSign Account</p>
                    <p className="text-sm text-muted-foreground">{integrations.docusign.email}</p>
                    <p className="text-xs text-muted-foreground font-mono">ID: {integrations.docusign.account_id}</p>
                  </div>
                  {disconnecting === "docusign" ? (
                    <div className="flex gap-2">
                      <Button type="button" variant="destructive" size="sm" onClick={() => disconnectIntegration("docusign")}>
                        Confirm
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={() => setDisconnecting(null)}>
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <Button type="button" variant="outline" onClick={() => setDisconnecting("docusign")}>
                      Disconnect
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Connect your DocuSign account to automatically send contracts when prospects accept proposals.
                  </p>
                  <Button 
                    className="bg-yellow-500 hover:bg-yellow-600 text-white"
                    onClick={() => initiateOAuth("docusign")}
                    disabled={connecting === "docusign"}
                    data-testid="connect-docusign-btn"
                  >
                    {connecting === "docusign" ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <ExternalLink className="w-4 h-4 mr-2" />
                    )}
                    Connect DocuSign
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Stripe Connect */}
          <Card className="border-stone">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle className="font-display text-xl">Stripe</CardTitle>
                    <CardDescription>Receive payments directly to your Stripe account</CardDescription>
                  </div>
                </div>
                {integrations.stripe.connected ? (
                  <Badge className="bg-green-100 text-green-800">
                    <Check className="w-3 h-3 mr-1" />
                    Connected
                  </Badge>
                ) : (
                  <Badge variant="secondary">Not Connected</Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {integrations.stripe.connected ? (
                <div className="flex items-center justify-between p-4 bg-sand rounded-lg">
                  <div>
                    <p className="font-medium text-forest">Stripe Connect Account</p>
                    <p className="text-sm text-muted-foreground font-mono">{integrations.stripe.account_id}</p>
                    <Badge variant="outline" className="mt-1">
                      {integrations.stripe.livemode ? "Live Mode" : "Test Mode"}
                    </Badge>
                  </div>
                  {disconnecting === "stripe" ? (
                    <div className="flex gap-2">
                      <Button type="button" variant="destructive" size="sm" onClick={() => disconnectIntegration("stripe")}>
                        Confirm
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={() => setDisconnecting(null)}>
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <Button type="button" variant="outline" onClick={() => setDisconnecting("stripe")}>
                      Disconnect
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Connect your Stripe account to receive invoice payments directly. We use Stripe Connect - we never see your banking details.
                  </p>
                  <Button 
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    onClick={() => initiateOAuth("stripe")}
                    disabled={connecting === "stripe"}
                    data-testid="connect-stripe-btn"
                  >
                    {connecting === "stripe" ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <ExternalLink className="w-4 h-4 mr-2" />
                    )}
                    Connect Stripe
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default Integrations;
